import React, { useEffect, useState } from "react";
import moment from "moment";
import { enqueueSnackbar } from "notistack";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../components/ui/card";
import { InputText } from "../../../components/InputText";
import { MilitarSelectField } from "../../../components/MilitarSelectField";
import { DatePickerField } from "../../../components/DatePickerField";
import { Label } from "../../../components/ui/label";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import {
  Shield,
  Clock,
  Users,
  CheckCircle,
  XCircle,
  AlertCircle,
  Calendar,
  Plus,
  Search,
  User,
  X,
  Loader2,
  Edit,
  TrendingUp,
  Settings,
  UserPlus,
  ChevronLeft,
  ChevronRight
} from "lucide-react";
import { api } from "../../../services/api";
import { useAuth } from "../../../contexts/AuthContext";
import { usePermissions, PERMISSOES } from "../../../hooks/usePermissions";
import { Form, Formik } from "formik";
import * as Yup from "yup";// Interfaces TypeScript
interface Patrulha {
  id: string;
  id_oficial: string;
  id_patrulheiro: string;
  inicio: string;
  termino?: string;
  trouxe?: string;
  status: "EA" | "CO" | "IN" | "CA";
  oficial: {
    nick: string;
    patente: {
      patente: string;
    };
  };
  patrulheiro: {
    nick: string;
    patente: {
      patente: string;
    };
  };
  criado_em: string;
}

interface Militar {
  id: string;
  nick: string;
  patente: string;
}

interface CreatePatrulhaData {
  id_patrulheiro: string;
}

interface UpdateStatusData {
  status: string;
  trouxe?: string;
}

interface PatrulhaModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface EditStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  patrulha: Patrulha | null;
  onSuccess?: () => void;
}

const createValidationSchema = Yup.object({
  id_patrulheiro: Yup.string().required("Selecione um patrulheiro"),
});

const updateStatusValidationSchema = Yup.object({
  status: Yup.string().required("Selecione um status"),
  trouxe: Yup.string().when("status", {
    is: "CO",
    then: (schema) => schema.required("Nick é obrigatório quando patrulha é concluída"),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const getStatusColor = (status: string) => {
  switch (status) {
    case "EA":
      return "bg-green-100 text-green-800 border-green-200 dark:bg-green-900/40 dark:text-green-300 dark:border-green-700";
    case "CO":
      return "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-700";
    case "IN":
      return "bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/40 dark:text-yellow-300 dark:border-yellow-700";
    case "CA":
      return "bg-red-100 text-red-800 border-red-200 dark:bg-red-900/40 dark:text-red-300 dark:border-red-700";
    default:
      return "bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800/40 dark:text-gray-300 dark:border-gray-600";
  }
};

const getStatusText = (status: string) => {
  switch (status) {
    case "EA":
      return "Em Andamento";
    case "CO":
      return "Concluída";
    case "IN":
      return "Incompleta";
    case "CA":
      return "Cancelada";
    default:
      return status;
  }
};

const getStatusIcon = (status: string) => {
  switch (status) {
    case "EA":
      return <Clock className="w-4 h-4" />;
    case "CO":
      return <CheckCircle className="w-4 h-4" />;
    case "IN":
      return <AlertCircle className="w-4 h-4" />;
    case "CA":
      return <XCircle className="w-4 h-4" />;
    default:
      return <Shield className="w-4 h-4" />;
  }
};

// Formatar data
const formatDate = (dateString: string) => {
  return moment(dateString).format("DD/MM/YYYY HH:mm");
};

function CriarPatrulhaModal({ isOpen, onClose, onSuccess }: PatrulhaModalProps) {
  const [loading, setLoading] = useState(false);
  const { militar } = useAuth();

  const handleSubmit = async (values: CreatePatrulhaData, { resetForm }: any) => {
    setLoading(true);
    try {
      const patrulhaData = {
        ...values,
        id_oficial: militar?.id,
        status: "EA", // Em Andamento
        inicio: new Date(), // Sempre usar o horário atual
      };

      await api.post("/patrulhas", patrulhaData);
      enqueueSnackbar("Patrulha criada com sucesso!", {
        variant: "success",
      });
      resetForm();
      onSuccess?.();
      onClose();
    } catch (error: any) {
      const message = error.response?.data?.message || "Erro ao criar patrulha";
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const initialValues: CreatePatrulhaData = {
    id_patrulheiro: "",
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-background border border-border rounded-lg shadow-lg w-full max-w-md">
        <Card className="border-0 shadow-none">
          <CardHeader className="border-b border-border">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-[oklch(0.45_0.15_142)]" />
                <CardTitle className="text-[oklch(0.35_0.15_142)] dark:text-foreground">
                  Criar Patrulha
                </CardTitle>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="h-6 w-6 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            <Formik
              initialValues={initialValues}
              validationSchema={createValidationSchema}
              onSubmit={handleSubmit}
            >
              {({ errors, setFieldValue }) => (
                <Form className="space-y-4">
                  <div>
                    <Label>Patrulheiro</Label>
                    <div className="mt-1">
                      <MilitarSelectField
                        placeholder="Digite o nick do patrulheiro..."
                        onChange={(militarId) => setFieldValue("id_patrulheiro", militarId)}
                      />
                    </div>
                    {errors.id_patrulheiro && (
                      <p className="text-sm text-red-600 mt-1">{errors.id_patrulheiro as string}</p>
                    )}
                  </div>

                  <div className="bg-blue-50 dark:bg-blue-950/50 p-3 rounded-lg border border-blue-200 dark:border-blue-800">
                    <div className="flex items-center gap-2 text-blue-800 dark:text-blue-300">
                      <Calendar className="w-4 h-4" />
                      <span className="text-sm font-medium">Data e Hora de Início</span>
                    </div>
                    <p className="text-sm text-blue-600 dark:text-blue-400 mt-1">
                      Será definida automaticamente como o horário atual
                    </p>
                  </div>

                  <div className="flex justify-end space-x-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={onClose}
                      disabled={loading}
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="submit"
                      disabled={loading}
                      className="bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Criando...
                        </>
                      ) : (
                        "Criar Patrulha"
                      )}
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function EditarStatusModal({ isOpen, onClose, patrulha, onSuccess }: EditStatusModalProps) {
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (values: UpdateStatusData, { resetForm }: any) => {
    if (!patrulha) return;

    setLoading(true);
    try {
      await api.patch(`/patrulhas/${patrulha.id}/status`, values);
      enqueueSnackbar("Status da patrulha atualizado com sucesso!", {
        variant: "success",
      });
      resetForm();
      onSuccess?.();
      onClose();
    } catch (error: any) {
      const message = error.response?.data?.message || "Erro ao atualizar status";
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const initialValues: UpdateStatusData = {
    status: patrulha?.status || "",
    trouxe: patrulha?.trouxe || "",
  };

  if (!isOpen || !patrulha) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-background border border-border rounded-lg shadow-lg w-full max-w-md">
        <Card className="border-0 shadow-none">
          <CardHeader className="border-b border-border">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Edit className="w-5 h-5 text-[oklch(0.45_0.15_142)]" />
                <CardTitle className="text-[oklch(0.35_0.15_142)] dark:text-foreground">
                  Alterar Status da Patrulha
                </CardTitle>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="h-6 w-6 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="mb-4 p-3 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">
                <strong>Patrulheiro:</strong> {patrulha.patrulheiro.patente.patente} {patrulha.patrulheiro.nick}
              </p>
              <p className="text-sm text-muted-foreground">
                <strong>Início:</strong> {formatDate(patrulha.inicio)}
              </p>
            </div>

            <Formik
              initialValues={initialValues}
              validationSchema={updateStatusValidationSchema}
              onSubmit={handleSubmit}
            >
              {({ errors, values, setFieldValue }) => (
                <Form className="space-y-4">
                  <div>
                    <Label htmlFor="status">Status</Label>
                    <select
                      id="status"
                      value={values.status}
                      onChange={(e) => setFieldValue("status", e.target.value)}
                      className="w-full mt-1 px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-[oklch(0.45_0.15_142)] focus:border-transparent"
                    >
                      <option value="EA">Em Andamento</option>
                      <option value="CO">Concluída</option>
                      <option value="IN">Incompleta</option>
                      <option value="CA">Cancelada</option>
                    </select>
                    {errors.status && (
                      <p className="text-sm text-red-600 mt-1">{errors.status as string}</p>
                    )}
                  </div>

                  {values.status === "CO" && (
                    <div>
                      <Label htmlFor="trouxe">Nick de quem trouxe</Label>
                      <input
                        id="trouxe"
                        name="trouxe"
                        type="text"
                        value={values.trouxe || ""}
                        onChange={(e) => setFieldValue("trouxe", e.target.value)}
                        placeholder="Digite o nick do jogador trazido"
                        className="w-full mt-1 px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-[oklch(0.45_0.15_142)] focus:border-transparent"
                      />
                      {errors.trouxe && (
                        <p className="text-sm text-red-600 mt-1">{errors.trouxe as string}</p>
                      )}
                    </div>
                  )}

                  <div className="flex justify-end space-x-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={onClose}
                      disabled={loading}
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="submit"
                      disabled={loading}
                      className="bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Salvando...
                        </>
                      ) : (
                        "Salvar Alterações"
                      )}
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export function Patrulhas() {
  const [patrulhas, setPatrulhas] = useState<Patrulha[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [patrulheiroFilter, setPatrulheiroFilter] = useState("");
  const [dataInicioFilter, setDataInicioFilter] = useState("");
  const [dataFimFilter, setDataFimFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const PAGE_SIZE = 50;
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedPatrulha, setSelectedPatrulha] = useState<Patrulha | null>(null);

  const { hasPermission } = usePermissions();
  const modoRestrito = !hasPermission(PERMISSOES.LISTAR_PATRULHAS);

  // Buscar patrulhas
  const fetchPatrulhas = async (trouxe?: string, status?: string, id_patrulheiro?: string, dataInicio?: string, dataFim?: string, currentPage = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (status) params.append("status", status);
      if (dataInicio) params.append("inicio", dataInicio);
      if (dataFim) params.append("termino", dataFim);
      params.append("page", String(currentPage));
      params.append("size", String(PAGE_SIZE));

      if (modoRestrito) {
        const url = `/patrulhas/minhas?${params.toString()}`;
        const response = await api.get(url);
        setPatrulhas(response.data.data);
        setTotalCount(response.data.totalCount);
        setTotalPages(response.data.totalPages);
      } else {
        if (trouxe) params.append("trouxe", trouxe);
        if (id_patrulheiro) params.append("id_patrulheiro", id_patrulheiro);
        const url = `/patrulhas?${params.toString()}`;
        const response = await api.get(url);
        setPatrulhas(response.data.data);
        setTotalCount(response.data.totalCount);
        setTotalPages(response.data.totalPages);
      }
    } catch (error) {
      enqueueSnackbar("Erro ao buscar patrulhas", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPatrulhas();
  }, []);

  // Handler para busca
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchPatrulhas(
      searchTerm || undefined,
      statusFilter || undefined,
      patrulheiroFilter || undefined,
      dataInicioFilter || undefined,
      dataFimFilter || undefined,
      1
    );
  };

  // Navegação de página
  const handlePrevPage = () => {
    if (page > 1) {
      const newPage = page - 1;
      setPage(newPage);
      fetchPatrulhas(
        searchTerm || undefined,
        statusFilter || undefined,
        patrulheiroFilter || undefined,
        dataInicioFilter || undefined,
        dataFimFilter || undefined,
        newPage
      );
    }
  };

  const handleNextPage = () => {
    if (page < totalPages) {
      const newPage = page + 1;
      setPage(newPage);
      fetchPatrulhas(
        searchTerm || undefined,
        statusFilter || undefined,
        patrulheiroFilter || undefined,
        dataInicioFilter || undefined,
        dataFimFilter || undefined,
        newPage
      );
    }
  };

  const handleGoToPage = (pageNum: number) => {
    setPage(pageNum);
    fetchPatrulhas(
      searchTerm || undefined,
      statusFilter || undefined,
      patrulheiroFilter || undefined,
      dataInicioFilter || undefined,
      dataFimFilter || undefined,
      pageNum
    );
  };

  // Abrir modal de editar status
  const handleEditStatus = (patrulha: Patrulha) => {
    setSelectedPatrulha(patrulha);
    setIsEditModalOpen(true);
  };

  // Fechar modal e recarregar dados
  const handleCloseModal = () => {
    setIsEditModalOpen(false);
    setSelectedPatrulha(null);
  };

  const handleUpdateSuccess = () => {
    fetchPatrulhas(
      searchTerm || undefined,
      statusFilter || undefined,
      patrulheiroFilter || undefined,
      dataInicioFilter || undefined,
      dataFimFilter || undefined,
      page
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="border-b border-border pb-4">
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
          <Shield className="h-6 w-6 text-[oklch(0.45_0.15_142)]" />
          Patrulhas
        </h1>
        <p className="text-muted-foreground mt-2">
          Gerencie e acompanhe as patrulhas do sistema
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Conteúdo Principal */}
        <div className="lg:col-span-2 space-y-6">

            {/* Filtros de Busca */}
            <Card className="border-l-4 border-l-[oklch(0.45_0.15_142)]">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-[oklch(0.35_0.15_142)]">
                  <Search className="w-5 h-5" />
                  Buscar Patrulhas
                </CardTitle>
                <CardDescription>
                  {modoRestrito
                    ? "Filtre por período de datas ou status para exibir suas próprias patrulhas"
                    : "Filtre por patrulheiro, nick de quem trouxe, período de datas ou status da patrulha"}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSearch} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                    {!modoRestrito && (
                    <div>
                      <Label>Patrulheiro</Label>
                      <div className="mt-1">
                        <MilitarSelectField
                          placeholder="Buscar patrulheiro..."
                          onChange={(militarId) => setPatrulheiroFilter(militarId)}
                        />
                      </div>
                    </div>
                    )}
                    {!modoRestrito && (
                    <div>
                      <Label htmlFor="search">Nick de quem trouxe</Label>
                      <InputText
                        id="search"
                        name="search"
                        title=""
                        placeholder="Digite o nick..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    )}
                    <div>
                      <DatePickerField
                        label="Data Início"
                        id="data-inicio"
                        name="data-inicio"
                        value={dataInicioFilter}
                        onChange={(value) => setDataInicioFilter(value)}
                      />
                    </div>
                    <div>
                      <DatePickerField
                        label="Data Fim"
                        id="data-fim"
                        name="data-fim"
                        value={dataFimFilter}
                        onChange={(value) => setDataFimFilter(value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="status">Status</Label>
                      <select
                        id="status"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="w-full mt-1 px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-[oklch(0.45_0.15_142)] focus:border-transparent"
                      >
                        <option value="">Todos os status</option>
                        <option value="EA">Em Andamento</option>
                        <option value="CO">Concluída</option>
                        <option value="IN">Incompleta</option>
                        <option value="CA">Cancelada</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="submit"
                      disabled={loading}
                      className="bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white"
                    >
                      {loading ? "Buscando..." : "Buscar"}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setStatusFilter("");
                        setDataInicioFilter("");
                        setDataFimFilter("");
                        setPage(1);
                        if (!modoRestrito) {
                          setSearchTerm("");
                          setPatrulheiroFilter("");
                        }
                        fetchPatrulhas(undefined, undefined, undefined, undefined, undefined, 1);
                      }}
                    >
                      Limpar Filtros
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            {/* Resultados */}
            <Card>
              <CardHeader>
                <CardTitle className="text-[oklch(0.35_0.15_142)]">Resultados</CardTitle>
                <CardDescription>
                  {totalCount} patrulha(s) encontrada(s)
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="text-muted-foreground">Carregando...</div>
                  </div>
                ) : patrulhas.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    Nenhuma patrulha encontrada
                  </div>
                ) : (
                  <>
                  <div className="space-y-4">
                    {patrulhas.map((patrulha) => (
                      <div
                        key={patrulha.id}
                        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 border border-border rounded-lg hover:bg-muted transition-colors"
                      >
                        {/* Avatares + info */}
                        <div className="flex items-start gap-3 min-w-0">
                          {/* Avatares */}
                          <div className="flex items-center gap-2 shrink-0">
                            <div className="relative">
                              <div className="w-12 h-12 bg-[oklch(0.45_0.15_142)]/10 rounded-lg flex items-center justify-center overflow-hidden">
                                <img
                                  src={`http://www.habbo.com.br/habbo-imaging/avatarimage?&user=${patrulha.patrulheiro.nick}&action=std&direction=5&head_direction=2&img_format=png&gesture=std&headonly=1&size=l`}
                                  alt={`Avatar de ${patrulha.patrulheiro.nick}`}
                                  className="max-w-full max-h-full object-contain"
                                  onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                    e.currentTarget.nextElementSibling?.classList.remove('hidden');
                                  }}
                                />
                                <Shield className="w-5 h-5 text-[oklch(0.45_0.15_142)] hidden" />
                              </div>
                              <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                                <Shield className="w-2 h-2 text-white" />
                              </div>
                            </div>

                            {patrulha.trouxe && (
                              <div className="relative">
                                <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center overflow-hidden">
                                  <img
                                    src={`http://www.habbo.com.br/habbo-imaging/avatarimage?&user=${patrulha.trouxe}&action=std&direction=5&head_direction=2&img_format=png&gesture=std&headonly=1&size=l`}
                                    alt={`Avatar de ${patrulha.trouxe}`}
                                    className="max-w-full max-h-full object-contain"
                                    onError={(e) => {
                                      e.currentTarget.style.display = 'none';
                                      e.currentTarget.nextElementSibling?.classList.remove('hidden');
                                    }}
                                  />
                                  <Users className="w-4 h-4 text-green-600 hidden" />
                                </div>
                                <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                                  <UserPlus className="w-2 h-2 text-white" />
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Texto */}
                          <div className="min-w-0 space-y-1">
                            <h3 className="font-semibold text-foreground">
                              {patrulha.patrulheiro.nick}
                            </h3>
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                              <Badge className={getStatusColor(patrulha.status)}>
                                <div className="flex items-center gap-1">
                                  {getStatusIcon(patrulha.status)}
                                  {getStatusText(patrulha.status)}
                                </div>
                              </Badge>
                              <span className="text-xs text-muted-foreground flex items-center gap-1 whitespace-nowrap">
                                <Calendar className="w-3 h-3 shrink-0" />
                                <span className="font-medium">Início:</span>&nbsp;{formatDate(patrulha.inicio)}
                              </span>
                              {patrulha.termino && (
                                <span className="text-xs text-muted-foreground flex items-center gap-1 whitespace-nowrap">
                                  <Clock className="w-3 h-3 shrink-0" />
                                  <span className="font-medium">Término:</span>&nbsp;{formatDate(patrulha.termino)}
                                </span>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-muted-foreground">
                              <span>Oficial: {patrulha.oficial.patente.patente} {patrulha.oficial.nick}</span>
                              {patrulha.trouxe && (
                                <span>Trouxe: {patrulha.trouxe}</span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Ação */}
                        <div className="flex items-center sm:shrink-0">
                          {patrulha.status === "EA" ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditStatus(patrulha)}
                              className="w-full sm:w-auto text-[oklch(0.45_0.15_142)] border-[oklch(0.45_0.15_142)]/20 hover:bg-[oklch(0.45_0.15_142)]/10 dark:text-[oklch(0.75_0.15_142)] dark:border-[oklch(0.45_0.15_142)]/40 dark:hover:bg-[oklch(0.45_0.15_142)]/20"
                            >
                              <Edit className="w-4 h-4 mr-2" />
                              Alterar Status
                            </Button>
                          ) : (
                            <Badge variant="secondary" className="text-xs">
                              Status Finalizado
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  {/* Controles de Paginação */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between pt-4 border-t border-border mt-4">
                      <div className="text-sm text-muted-foreground">
                        Página {page} de {totalPages} ({totalCount} registros)
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handlePrevPage}
                          disabled={page === 1 || loading}
                          className="flex items-center gap-1"
                        >
                          <ChevronLeft className="h-4 w-4" />
                          Anterior
                        </Button>
                        <div className="flex items-center gap-1">
                          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            let pageNum;
                            if (totalPages <= 5) {
                              pageNum = i + 1;
                            } else if (page <= 3) {
                              pageNum = i + 1;
                            } else if (page >= totalPages - 2) {
                              pageNum = totalPages - 4 + i;
                            } else {
                              pageNum = page - 2 + i;
                            }
                            return (
                              <Button
                                key={pageNum}
                                variant={page === pageNum ? "default" : "outline"}
                                size="sm"
                                onClick={() => handleGoToPage(pageNum)}
                                disabled={loading}
                                className={page === pageNum ? "bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)]" : ""}
                              >
                                {pageNum}
                              </Button>
                            );
                          })}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleNextPage}
                          disabled={page === totalPages || loading}
                          className="flex items-center gap-1"
                        >
                          Próximo
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                  </>
                )}
              </CardContent>
            </Card>
        </div>

        {/* Painel Lateral */}
        <div className="space-y-6">
            {/* Estatísticas */}
            <Card className="border-l-4 border-l-[oklch(0.45_0.15_142)]">
              <CardHeader>
                <CardTitle className="text-[oklch(0.35_0.15_142)] dark:text-foreground flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Estatísticas
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Total de Patrulhas</span>
                    <Badge className="bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-700">
                      {totalCount}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Em Andamento</span>
                    <Badge className="bg-green-100 text-green-800 border-green-200 dark:bg-green-900/40 dark:text-green-300 dark:border-green-700">
                      {patrulhas.filter(p => p.status === "EA").length}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Concluídas</span>
                    <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-300 dark:border-emerald-700">
                      {patrulhas.filter(p => p.status === "CO").length}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Incompletas/Canceladas</span>
                    <Badge className="bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-700">
                      {patrulhas.filter(p => p.status === "IN" || p.status === "CA").length}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Ações Rápidas */}
            {hasPermission(PERMISSOES.CRIAR_PATRULHA) && (
            <Card>
              <CardHeader>
                <CardTitle className="text-[oklch(0.35_0.15_142)] dark:text-foreground flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Ações Rápidas
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <Button
                    onClick={() => setIsCreateModalOpen(true)}
                    className="w-full justify-start bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Nova Patrulha
                  </Button>
                </div>
              </CardContent>
            </Card>
            )}
        </div>
      </div>

      {/* Modal Criar Patrulha */}
      <CriarPatrulhaModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={() => {
          setIsCreateModalOpen(false);
          setPage(1);
          fetchPatrulhas(
            searchTerm || undefined,
            statusFilter || undefined,
            patrulheiroFilter || undefined,
            dataInicioFilter || undefined,
            dataFimFilter || undefined,
            1
          );
        }}
      />

      {/* Modal Editar Status */}
      {selectedPatrulha && (
        <EditarStatusModal
          isOpen={isEditModalOpen}
          onClose={handleCloseModal}
          patrulha={selectedPatrulha}
          onSuccess={handleUpdateSuccess}
        />
      )}
    </div>
  );
}
